import type { RefObject } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import SearchFilter from '@assets/icons/filter.svg';
import SearchIcon from '@assets/icons/search.svg';
import CheckboxMenuType from '@components/filter-checkbox-selector/CheckboxMenuType.tsx';
import { FILTER_MENU_TYPES } from '@components/filter-checkbox-selector/constants.ts';
import type { SelectedLeafInfo } from '@components/filter-checkbox-selector/helpers.ts';
import { getSubmenuByPath, getSubmenuTypeByPath } from '@components/filter-checkbox-selector/helpers.ts';
import RadioMenuType from '@components/filter-checkbox-selector/RadioMenuType.tsx';
import { BaseTag } from '@components/tag';
import { SURFACE_COLORS } from '@constants/colors.ts';
import { useClickOutside } from '@hooks/useClickOutside.ts';
import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import type { CommonSearchRequest } from '@types';
import { translateItem } from '@utils/translations.ts';
import { isEmpty } from 'lodash';

import {
    ChevronIconRight,
    ChevronToggleIcon,
    ClearAll,
    CloseIconWrapper,
    DropDown,
    DropDownInput,
    DropDownMenu,
    DropDownMenuLeftItems,
    DropDownMenuRollout,
    DropDownPlaceholder,
    DropDownSearchField,
    DropDownSearchFieldIcon,
    DropDownSearchFieldToggler,
    FilterHeaderWrapper,
    FilterSearchAction,
    FilterSearchFilter,
    FiltersList,
    FilterTitle,
    FilterWrapperLeft,
    MenuLeftItem,
    MenuLeftItemIco,
    MenuLeftItemText,
    Search,
    SearchFieldWrapper,
    TagChild,
    TagParent,
} from './styled.ts';

export interface FilterSearchPropsMenu {
    name: string;
    displayName: string | MessageDescriptor;
    submenu: FilterSearchPropsMenu[];
    isSelected: boolean;
    menuType?: (typeof FILTER_MENU_TYPES)[keyof typeof FILTER_MENU_TYPES];
}

export interface FilterSearchProps {
    menu: FilterSearchPropsMenu[];
    clearAll: boolean;
    inputValue: string;
    searchHintText: string;
    filtersCount: number;
    onClearAllFilters: () => void;
    onCheckboxSelected: (
        path: number[] | number[][],
        checked: boolean,
        overriddenMenu?: FilterSearchPropsMenu[],
    ) => void;
    onClearSearch: () => void;
    onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    submenuRenderSide?: 'right' | 'left';
    hasCustomFiltersList?: boolean;
    requestData?: CommonSearchRequest;
    selectedLeaves?: SelectedLeafInfo[];
}

export const FilterSearch = ({
    menu,
    onCheckboxSelected,
    onChangeSearch,
    onClearSearch,
    clearAll,
    inputValue,
    searchHintText,
    onClearAllFilters,
    filtersCount,
    requestData,
    selectedLeaves,
    submenuRenderSide = 'right',
    hasCustomFiltersList = false,
}: FilterSearchProps) => {
    const { t } = useLingui();
    const [isDropdownActive, setIsDropdownActive] = useState(false);
    const [activePath, setActivePath] = useState<number[]>([]);
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const handleClearAllInternal = () => {
        setIsSearchFocused(false);
        setIsDropdownActive(false);
        setActivePath([]);
    };

    useEffect(() => {
        if (clearAll) {
            handleClearAllInternal();
        }
    }, [clearAll]);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation(); //what were the issues that we need to stop propagating the event?
        setIsDropdownActive((prev) => {
            if (prev) setActivePath([]);
            return !prev;
        });
    };

    useClickOutside(containerRef as RefObject<HTMLElement>, () => {
        setIsDropdownActive(false);
        setActivePath([]);
    });

    const handleLeftClick = (level: number, index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newPath = [...activePath.slice(0, level), index];
        setActivePath(newPath);
        if (!isDropdownActive) setIsDropdownActive(true);
    };

    const handleSingleCheckboxSelected = (path: number[] | number[][], e?: any) => {
        let checked: boolean;
        const singlePath = path as number[];

        if (typeof e.target?.checked === 'boolean') {
            checked = e.target.checked;
        } else {
            const parentItems = singlePath.length === 1 ? menu : getSubmenuByPath(menu, singlePath.slice(0, -1));
            const node = parentItems[singlePath[singlePath.length - 1]];
            checked = !node.isSelected;
        }

        onCheckboxSelected?.(singlePath, checked);
    };

    const handleSelectAllSelected = (path: number[] | number[][]) => {
        const paths = path as number[][];
        const leafItemsParent = getSubmenuByPath(menu, activePath);
        const isLeafLevel = leafItemsParent.every((i) => i.submenu.length === 0);
        const items = isLeafLevel ? leafItemsParent : [];
        const allSelected = items.length > 0 && items.every((i) => i.isSelected);
        const allItemsSelectedNextState = !allSelected;

        onCheckboxSelected?.(paths, allItemsSelectedNextState);
    };

    const handleLeafToggle = (path: number[] | number[][], e?: any) => {
        if (e?.stopPropagation) e.stopPropagation();

        const isSingleCheckboxSelected = Array.isArray(path) && !Array.isArray((path as any)[0]);

        if (isSingleCheckboxSelected) {
            handleSingleCheckboxSelected(path, e);
        } else {
            handleSelectAllSelected(path);
        }
    };

    const renderMenuBasedOnMenuType = (
        menuType: (typeof FILTER_MENU_TYPES)[keyof typeof FILTER_MENU_TYPES] | undefined,
        level: number,
        items: FilterSearchPropsMenu[],
    ) => {
        if (menuType === undefined) return null;

        if (menuType === FILTER_MENU_TYPES.Radio) {
            return (
                <RadioMenuType
                    level={level}
                    items={items}
                    activePath={activePath}
                    onCheckboxSelected={onCheckboxSelected}
                    menu={menu}
                />
            );
        } else if (menuType === FILTER_MENU_TYPES.Checkbox) {
            return (
                <CheckboxMenuType
                    level={level}
                    items={items}
                    activePath={activePath}
                    handleLeafToggle={handleLeafToggle}
                />
            );
        }
    };

    return (
        <>
            <FilterWrapperLeft ref={containerRef}>
                <FilterSearchAction>
                    <FilterSearchFilter>
                        <Search $active={isSearchFocused}>
                            <SearchFieldWrapper
                                prefixIcon={<SearchIcon />}
                                type="text"
                                allowClear
                                placeholder={searchHintText}
                                value={inputValue}
                                onChange={onChangeSearch}
                                onClear={onClearSearch}
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}
                            />
                        </Search>

                        <DropDown>
                            <DropDownInput $active={isDropdownActive}>
                                <DropDownSearchField onClick={handleToggle}>
                                    <DropDownSearchFieldIcon>
                                        <SearchFilter />
                                    </DropDownSearchFieldIcon>
                                    <DropDownPlaceholder>{t`Filter`}</DropDownPlaceholder>
                                    <DropDownSearchFieldToggler>
                                        <ChevronToggleIcon $rotated={isDropdownActive} />
                                    </DropDownSearchFieldToggler>
                                </DropDownSearchField>
                            </DropDownInput>

                            <DropDownMenu $show={isDropdownActive} $submenuRenderSide={submenuRenderSide}>
                                {Array.from({ length: activePath.length + 1 }, (_, level) => level).map((level) => {
                                    const items =
                                        level === 0 ? menu : getSubmenuByPath(menu, activePath.slice(0, level));
                                    const menuType = getSubmenuTypeByPath(menu, activePath.slice(0, level));

                                    const isLeafLevel = items.every((i) => i.submenu.length === 0);

                                    if (isLeafLevel) {
                                        return (
                                            <React.Fragment key={`leaf-${level}`}>
                                                {renderMenuBasedOnMenuType(menuType, level, items)}
                                            </React.Fragment>
                                        );
                                    }

                                    return (
                                        <DropDownMenuRollout key={level}>
                                            <DropDownMenuLeftItems>
                                                {items.map((item, idx) => (
                                                    <MenuLeftItem
                                                        key={translateItem(item.displayName as string)}
                                                        onClick={(e) => handleLeftClick(level, idx, e)}
                                                    >
                                                        <MenuLeftItemText>
                                                            {translateItem(item.displayName as string)}
                                                        </MenuLeftItemText>
                                                        {item.submenu.length > 0 && (
                                                            <MenuLeftItemIco>
                                                                <ChevronIconRight />
                                                            </MenuLeftItemIco>
                                                        )}
                                                    </MenuLeftItem>
                                                ))}
                                            </DropDownMenuLeftItems>
                                        </DropDownMenuRollout>
                                    );
                                })}
                            </DropDownMenu>
                        </DropDown>
                    </FilterSearchFilter>
                </FilterSearchAction>
            </FilterWrapperLeft>
            {filtersCount > 0 && (
                <FilterHeaderWrapper>
                    <FilterTitle>
                        {t`Filter`}: {filtersCount}
                    </FilterTitle>
                    <ClearAll onClick={onClearAllFilters}>
                        <BaseTag key="clearAll" variant="filled" color="transparent">
                            <TagParent>{t`Clear All`}</TagParent>
                        </BaseTag>
                    </ClearAll>
                </FilterHeaderWrapper>
            )}
            {!!(!hasCustomFiltersList && requestData && selectedLeaves) && (
                <FiltersList>
                    {!isEmpty(requestData.textSearch) && (
                        <BaseTag
                            key="search"
                            variant="filled"
                            closable
                            color={SURFACE_COLORS.page}
                            closeIcon={<CloseIconWrapper />}
                            onClose={(e) => {
                                e.preventDefault();
                                onClearSearch();
                            }}
                        >
                            <TagChild>{requestData.textSearch}</TagChild>
                        </BaseTag>
                    )}
                    {selectedLeaves.map(({ path, parent, child }) => (
                        <BaseTag
                            key={path.join('-')}
                            variant="filled"
                            closable
                            color={SURFACE_COLORS.page}
                            closeIcon={<CloseIconWrapper />}
                            onClose={(e) => {
                                e.preventDefault();
                                onCheckboxSelected(path, false);
                            }}
                        >
                            <TagParent>{parent}: </TagParent>
                            <TagChild>{child}</TagChild>
                        </BaseTag>
                    ))}
                </FiltersList>
            )}
        </>
    );
};
